import { Injectable, inject, signal, Signal, OnDestroy } from '@angular/core';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  DocumentData,
  QueryDocumentSnapshot,
  getFirestore,
  QueryFieldFilterConstraint,
  QueryOrderByConstraint,
  QueryLimitConstraint,
  QueryStartAtConstraint,
} from 'firebase/firestore';
import { ProfileService } from '../profile/profile.service';

@Injectable({
  providedIn: 'root',
})
export class RoomListService implements OnDestroy {
  private readonly profileService = inject(ProfileService);
  private readonly db = getFirestore();
  private readonly pageSize = 20;

  private readonly hasMore = signal(true);
  private readonly rooms = signal<string[]>([]);
  private lastRoomDocument: QueryDocumentSnapshot<DocumentData> | undefined;
  private initialised = false;

  get hasMoreRooms(): Signal<boolean> {
    return this.hasMore.asReadonly();
  }

  get roomIds(): Signal<string[]> {
    return this.rooms.asReadonly();
  }

  async initialise(gameId: string): Promise<void> {
    if (this.initialised) {
      return;
    }
    this.initialised = true;
    await this.moreRooms(gameId);
  }

  ngOnDestroy(): void {
    this.lastRoomDocument = undefined;
    this.rooms.set([]);
    this.hasMore.set(true);
  }

  async addRoom(roomId: string, gameId: string): Promise<void> {
    await this.initialise(gameId);
    if (this.rooms().includes(roomId)) {
      return;
    }
    this.rooms.update((rooms) => [roomId, ...rooms]);
  }

  async moreRooms(gameId: string): Promise<void> {
    this.initialised = true;

    try {
      // Create base query
      const roomsRef = collection(this.db, 'matches', this.profileService.userId, 'rooms');

      // Build query constraints
      const constraints: (
        | QueryFieldFilterConstraint
        | QueryOrderByConstraint
        | QueryLimitConstraint
        | QueryStartAtConstraint
      )[] = [where('gameId', '==', gameId), orderBy('sortDate', 'desc'), limit(this.pageSize + 1)];

      // Add startAfter if we have a last document
      if (this.lastRoomDocument) {
        constraints.push(startAfter(this.lastRoomDocument));
      }

      // Create and execute query
      const q = query(roomsRef, ...constraints);
      const rooms = (await getDocs(q)).docs;
      this.hasMore.set(rooms.length > this.pageSize);

      // Store the last document for pagination, accounting for the extra item we fetched
      this.lastRoomDocument =
        rooms.length > 0 ? rooms[this.hasMore() ? rooms.length - 2 : rooms.length - 1] : undefined;

      // Add new unique room IDs
      const existingIds = new Set(this.rooms());
      const newRoomIds = rooms.map((room) => room.id).filter((id) => !existingIds.has(id));

      if (newRoomIds.length > 0) {
        this.rooms.update((rooms) => [...rooms, ...newRoomIds]);
      }
    } catch (error) {
      this.hasMore.set(false);
      throw error;
    }
  }
}
